import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Wrench, ChevronDown, ChevronRight, Sparkles, Target, Rocket, Clock, Lightbulb, Zap, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const WEEK_PALETTE = [
  {
    from: "from-emerald",
    to: "to-teal",
    soft: "bg-emeraldlight",
    text: "text-emerald",
    ring: "ring-emerald/40",
    chipBg: "bg-emerald",
    bar: "bg-emerald",
    glow: "shadow-emerald/40",
  },
  {
    from: "from-sky",
    to: "to-indigo-500",
    soft: "bg-skylight",
    text: "text-sky",
    ring: "ring-sky/40",
    chipBg: "bg-sky",
    bar: "bg-sky",
    glow: "shadow-sky/40",
  },
  {
    from: "from-rose",
    to: "to-pink-500",
    soft: "bg-roselight",
    text: "text-rose",
    ring: "ring-rose/40",
    chipBg: "bg-rose",
    bar: "bg-rose",
    glow: "shadow-rose/40",
  },
  {
    from: "from-terracotta",
    to: "to-amber-500",
    soft: "bg-lemonlight",
    text: "text-terracotta",
    ring: "ring-terracotta/40",
    chipBg: "bg-terracotta",
    bar: "bg-terracotta",
    glow: "shadow-terracotta/40",
  },
  {
    from: "from-violet",
    to: "to-purple-600",
    soft: "bg-violet/15",
    text: "text-violet",
    ring: "ring-violet/40",
    chipBg: "bg-violet",
    bar: "bg-violet",
    glow: "shadow-violet/40",
  },
  {
    from: "from-lemon",
    to: "to-amber-400",
    soft: "bg-lemon/30",
    text: "text-amber-600",
    ring: "ring-amber-400/40",
    chipBg: "bg-amber-500",
    bar: "bg-amber-500",
    glow: "shadow-amber-400/40",
  },
];

const WEEK_ICONS = [Sparkles, Target, Lightbulb, Zap, Rocket, Star];

export default function Curriculum({ weeks, testId = "curriculum" }) {
  const [openIdx, setOpenIdx] = useState(0);
  if (!weeks || weeks.length === 0) return null;

  return (
    <div data-testid={testId} className="relative">
      <div className="hidden md:block absolute left-9 top-4 bottom-4 w-[3px] bg-gradient-to-b from-emerald via-sky to-violet rounded-full opacity-60" aria-hidden="true" />

      <div className="space-y-6 md:space-y-7">
        {weeks.map((wk, i) => {
          const palette = WEEK_PALETTE[i % WEEK_PALETTE.length];
          const Icon = WEEK_ICONS[i % WEEK_ICONS.length];
          const isOpen = openIdx === i;
          const progressPct = ((i + 1) / weeks.length) * 100;

          return (
            <Reveal key={wk.week} delay={i * 0.05}>
              <motion.div
                layout
                className="relative group"
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.04 }}
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="relative shrink-0 flex-col items-center hidden md:flex">
                    <motion.div
                      className={`relative w-18 h-18 rounded-2xl bg-gradient-to-br ${palette.from} ${palette.to} p-[2px] shadow-lg ${palette.glow} group-hover:shadow-2xl transition-shadow duration-500`}
                      whileHover={{ y: -3, rotate: -3 }}
                    >
                      <div className="w-full h-full rounded-[calc(1rem-1px)] bg-surface flex flex-col items-center justify-center">
                        <Icon size={18} className={palette.text} />
                        <span className={`mt-1 font-display font-black text-[10px] uppercase tracking-wider ${palette.text}`}>W{i + 1}</span>
                      </div>
                    </motion.div>
                    {i < weeks.length - 1 && (
                      <div className={`w-[3px] mt-2 flex-1 ${palette.soft} rounded-full`} style={{ height: "calc(100% + 12px)" }} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <motion.div
                      layout
                      className={`rounded-3xl bg-surface shadow-sm overflow-hidden border border-line hover:shadow-xl hover:-translate-y-0.5 transition-all duration-500 ring-1 ring-transparent hover:${palette.ring}`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenIdx(isOpen ? -1 : i)}
                        className="w-full text-left"
                      >
                        <div className={`flex items-center gap-4 px-5 md:px-7 py-5 md:py-6 ${palette.soft}/60 border-b border-line/70 relative overflow-hidden`}>
                          <div className={`absolute inset-0 bg-gradient-to-r ${palette.from}/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700`} aria-hidden="true" />
                          <div className={`md:hidden shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${palette.from} ${palette.to} flex items-center justify-center shadow-md`}>
                            <Icon size={16} className="text-white" />
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <motion.span
                              className={`font-display font-black text-2xl md:text-3xl leading-none bg-gradient-to-br ${palette.from} ${palette.to} bg-clip-text text-transparent`}
                            >
                              {wk.week}
                            </motion.span>
                            <span className={`h-7 w-px ${palette.text}/30`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-display font-semibold text-lg md:text-xl text-ink leading-snug truncate">{wk.theme}</h4>
                          </div>
                          <div className="hidden sm:flex items-center gap-2 shrink-0">
                            <Clock size={14} className={palette.text} />
                            <span className={`font-sans text-xs font-bold uppercase tracking-wider ${palette.text}`}>
                              {wk.items.length} sessions
                            </span>
                          </div>
                          <motion.div
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className={`shrink-0 w-9 h-9 rounded-full ${palette.soft} flex items-center justify-center`}
                          >
                            <ChevronDown size={16} className={palette.text} />
                          </motion.div>
                        </div>

                        <div className="px-5 md:px-7 py-3 border-b border-line/70 bg-paper/60 hidden sm:block">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-1.5 rounded-full bg-ink/5 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${progressPct}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.1, delay: 0.2 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                className={`h-full rounded-full ${palette.bar}`}
                              />
                            </div>
                            <span className={`font-sans text-[11px] font-bold ${palette.text}`}>
                              {Math.round(progressPct)}%
                            </span>
                          </div>
                        </div>
                      </button>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="divide-y divide-line/60 px-5 md:px-7">
                              {wk.items.map((item, j) => (
                                <motion.div
                                  key={j}
                                  initial={{ opacity: 0, x: -8 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.35, delay: 0.05 + j * 0.06 }}
                                  className="py-5 first:pt-5 last:pb-6"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5">
                                    <div className="flex items-center gap-3 sm:w-2/3">
                                      <span className={`shrink-0 w-8 h-8 rounded-xl ${palette.soft} flex items-center justify-center`}>
                                        <ChevronRight size={14} className={palette.text} />
                                      </span>
                                      <p className="font-sans font-semibold text-[15px] md:text-base text-ink leading-snug">
                                        {item.title}
                                      </p>
                                    </div>
                                    {item.deliverable && (
                                      <div className="sm:w-1/3 flex sm:justify-end">
                                        <motion.div
                                          whileHover={{ scale: 1.03, x: 2 }}
                                          className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 ${palette.chipBg} text-white shadow-md ${palette.glow}`}
                                        >
                                          <CheckCircle2 size={12} />
                                          <span className="font-sans text-[11px] font-bold uppercase tracking-wider whitespace-nowrap">
                                            {item.deliverable}
                                          </span>
                                        </motion.div>
                                      </div>
                                    )}
                                  </div>
                                  {item.desc && (
                                    <p className="mt-3 pl-11 text-graphite text-[14px] leading-relaxed max-w-2xl">
                                      {item.desc}
                                    </p>
                                  )}
                                  {item.tools && item.tools.length > 0 && (
                                    <div className="mt-3 pl-11 flex flex-wrap items-center gap-2">
                                      <Wrench size={12} className="text-graphite shrink-0" />
                                      {item.tools.map((t, k) => (
                                        <motion.span
                                          key={k}
                                          whileHover={{ y: -1, scale: 1.05 }}
                                          className="inline-block rounded-full bg-stone px-3 py-1 text-[11px] font-sans font-semibold text-ink/80 border border-line/60 shadow-sm"
                                        >
                                          {t}
                                        </motion.span>
                                      ))}
                                    </div>
                                  )}
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}
